/**
 * Pendências de vacinação (#530, spec D10): uma linha por funcionário × vacina exigida fora do
 * "em dia", nas empresas que a pessoa acompanha. Filtro e paginação são do servidor; clicar na
 * linha leva à ficha, onde a dose se registra.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Typography } from '@mui/material';
import {
  DataTable,
  InlineFilterBar,
  InlineSelect,
  InlineToggles,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { CompanySelectField } from '../../components/forms/CompanySelectField';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { departmentService } from '../../services/departmentService';
import { unitService } from '../../services/unitService';
import { vaccinationService } from '../../services/vaccinationService';
import { vaccineTypeService } from '../../services/vaccineTypeService';
import { getApiErrorMessage } from '../../types/common';
import type { Department } from '../../types/department';
import type { Unit } from '../../types/unit';
import {
  SITUACAO_VACINAL_LABELS,
  type FiltrosDePendencia,
  type LinhaDePendencia,
  type SituacaoVacinal,
  type VaccineType,
} from '../../types/vaccination';
import { dataBrasileira } from '../../utils/vaccinationDates';
import { SituacaoVacinalChip } from '../employees/components/EmployeeVaccinationsCard';

const breadcrumbItems = [
  { label: 'Início', path: '/home' },
  { label: 'Medicina' },
  { label: 'Pendências de vacinação' },
];

/** "Em dia" não é pendência: a tela só filtra entre as três situações que pedem ação. */
const SITUACOES_FILTRAVEIS: readonly SituacaoVacinal[] = ['A_VENCER', 'PENDENTE', 'VENCIDA'];
const SITUACOES_PADRAO: readonly SituacaoVacinal[] = ['PENDENTE', 'VENCIDA'];
const LINHAS_POR_PAGINA = 25;

interface RespostaDaConsulta {
  consulta: string;
  linhas: LinhaDePendencia[];
  total: number;
  erro: string | null;
}

/** Sem data, só a pendente é "sem dose"; a vencer não tem "desde", porque ainda não atrasou. */
function desdeDaPendencia(linha: LinhaDePendencia): string {
  if (linha.desde) return dataBrasileira(linha.desde);
  return linha.situacao === 'PENDENTE' ? 'sem dose' : '—';
}

const columns: DataTableColumn<LinhaDePendencia>[] = [
  { id: 'employeeName', label: 'Funcionário', primary: true, render: (linha) => linha.employeeName },
  { id: 'departmentName', label: 'Setor', render: (linha) => linha.departmentName ?? '—' },
  { id: 'unitName', label: 'Unidade', render: (linha) => linha.unitName ?? '—' },
  { id: 'vaccineName', label: 'Vacina', render: (linha) => linha.vaccineName },
  {
    id: 'desde',
    label: 'Desde',
    render: desdeDaPendencia,
  },
  { id: 'situacao', label: 'Situação', render: (linha) => <SituacaoVacinalChip situacao={linha.situacao} /> },
];

function mesmasSituacoes(umas: readonly SituacaoVacinal[], outras: readonly SituacaoVacinal[]): boolean {
  return umas.length === outras.length && umas.every((situacao) => outras.includes(situacao));
}

export const VaccinationPendenciesPage: React.FC = () => {
  const navigate = useNavigate();
  const abrirFicha = useControle('/employees/:id', pode('employee.read'), 'Abrir ficha');
  const marcarExigidas = useAcao('vaccine-requirement.update', 'Vacinas exigidas');
  const { effectiveCompanyIdString, shouldShowCompanyField } = useCompanyContext();

  const [empresaEscolhida, setEmpresaEscolhida] = useState<string | null>(null);
  const companyId = empresaEscolhida ?? effectiveCompanyIdString;
  const [unitId, setUnitId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [vaccineTypeId, setVaccineTypeId] = useState('');
  const [situacoes, setSituacoes] = useState<readonly SituacaoVacinal[]>(SITUACOES_PADRAO);
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(LINHAS_POR_PAGINA);

  // A resposta guarda a consulta que a produziu: enquanto ela não é a atual, a tela está carregando.
  const [resposta, setResposta] = useState<RespostaDaConsulta | null>(null);

  const carregarUnidades = useCallback(() => unitService.getUnitsByCompanyId(companyId), [companyId]);
  const carregarSetores = useCallback(() => departmentService.getDepartmentsByCompanyId(companyId), [companyId]);
  const carregarVacinas = useCallback(() => vaccineTypeService.getAll(), []);
  const unidades = useListaPermitida<Unit>('unit.read', 'Unidades', carregarUnidades, { habilitado: Boolean(companyId) });
  const setores = useListaPermitida<Department>('department.read', 'Setores', carregarSetores, {
    habilitado: Boolean(companyId),
  });
  const vacinas = useListaPermitida<VaccineType>('vaccine-type.read', 'Vacinas', carregarVacinas);

  const filtros = useMemo<FiltrosDePendencia>(
    () => ({
      companyId: companyId || undefined,
      unitId: unitId || undefined,
      departmentId: departmentId || undefined,
      vaccineTypeId: vaccineTypeId || undefined,
      situacao: [...situacoes],
      search: busca || undefined,
      page: page + 1,
      limit: rowsPerPage,
    }),
    [companyId, unitId, departmentId, vaccineTypeId, situacoes, busca, page, rowsPerPage]
  );
  const consulta = JSON.stringify(filtros);

  useEffect(() => {
    let cancelado = false;
    vaccinationService
      .getPendencies(filtros)
      .then((pagina) => {
        if (!cancelado) setResposta({ consulta, linhas: pagina.rows, total: pagina.total, erro: null });
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        const erro = getApiErrorMessage(err, 'Não foi possível carregar as pendências de vacinação.');
        setResposta({ consulta, linhas: [], total: 0, erro });
      });
    return () => {
      cancelado = true;
    };
  }, [filtros, consulta]);

  const carregando = resposta?.consulta !== consulta;
  const linhas = resposta?.linhas ?? [];
  const total = resposta?.total ?? 0;
  const erro = carregando ? null : resposta?.erro ?? null;

  const filtrar = <T,>(aplicar: (valor: T) => void) => (valor: T) => {
    aplicar(valor);
    setPage(0);
  };

  const alternarSituacao = (situacao: SituacaoVacinal, marcada: boolean) => {
    const proximas = marcada ? [...situacoes, situacao] : situacoes.filter((atual) => atual !== situacao);
    // Sem nenhuma marcada o servidor voltaria ao padrão calado; a última fica presa.
    if (proximas.length === 0) return;
    setSituacoes(SITUACOES_FILTRAVEIS.filter((atual) => proximas.includes(atual)));
    setPage(0);
  };

  const filtrosAtivos =
    Boolean(unitId || departmentId || vaccineTypeId || busca) || !mesmasSituacoes(situacoes, SITUACOES_PADRAO);

  const limparFiltros = () => {
    setUnitId('');
    setDepartmentId('');
    setVaccineTypeId('');
    setBusca('');
    setSituacoes(SITUACOES_PADRAO);
    setPage(0);
  };

  const trocarEmpresa = (valor: string) => {
    setEmpresaEscolhida(valor);
    setUnitId('');
    setDepartmentId('');
    setPage(0);
  };

  const semNinguemNoPadrao = !carregando && !erro && linhas.length === 0 && !filtrosAtivos;

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      title="Pendências de vacinação"
      subtitle="Quem está com vacina exigida pendente, vencida ou a vencer. Clique na linha para registrar a dose na ficha."
    >
      {shouldShowCompanyField && (
        <Box sx={{ mb: 3 }}>
          <CompanySelectField value={companyId} onChange={trocarEmpresa} label="Empresa" />
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <InlineFilterBar
          searchPlaceholder="Buscar funcionário pelo nome"
          searchValue={busca}
          onSearchChange={filtrar(setBusca)}
          onClearAll={filtrosAtivos ? limparFiltros : undefined}
          resultCount={total}
          resultNoun="pendências"
          loading={carregando}
        >
          <InlineToggles
            items={SITUACOES_FILTRAVEIS.map((situacao) => ({
              label: SITUACAO_VACINAL_LABELS[situacao],
              checked: situacoes.includes(situacao),
              onChange: (marcada: boolean) => alternarSituacao(situacao, marcada),
            }))}
          />
          <InlineSelect
            label="Vacina"
            value={vaccineTypeId}
            options={vacinas.itens.map((vacina) => ({ value: String(vacina.id), label: vacina.name }))}
            onChange={filtrar(setVaccineTypeId)}
            emptyLabel="Todas"
            disabled={!vacinas.permitido}
            minWidth={160}
          />
          <InlineSelect
            label="Unidade"
            value={unitId}
            options={unidades.itens.map((unidade) => ({ value: String(unidade.id), label: unidade.name }))}
            onChange={filtrar(setUnitId)}
            emptyLabel="Todas"
            disabled={!companyId || !unidades.permitido}
            minWidth={150}
          />
          <InlineSelect
            label="Setor"
            value={departmentId}
            options={setores.itens.map((setor) => ({ value: String(setor.id), label: setor.name }))}
            onChange={filtrar(setDepartmentId)}
            emptyLabel="Todos"
            disabled={!companyId || !setores.permitido}
            minWidth={150}
          />
        </InlineFilterBar>
      </Box>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={linhas}
        getRowId={(linha) => `${linha.employeeId}-${linha.vaccineTypeId}`}
        loading={carregando}
        emptyState={{
          title: 'Ninguém com vacina pendente ou vencida nas empresas que você acompanha',
          description:
            'A lista vem das vacinas que cada empresa exige, marcadas em Vacinas exigidas na ficha da empresa. Empresa que não exige nenhuma vacina não entra nesta conta.',
        }}
        filtersActive={filtrosAtivos}
        onClearFilters={limparFiltros}
        onRowClick={abrirFicha.disabled ? undefined : (linha) => navigate(`/employees/${linha.employeeId}`)}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: setPage,
          onRowsPerPageChange: (valor) => {
            setRowsPerPage(valor);
            setPage(0);
          },
        }}
      />

      {semNinguemNoPadrao && !marcarExigidas.disabled && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            As vacinas exigidas se marcam em cada empresa.
          </Typography>
          <RouteActionButton para="/companies" exige={pode('company.read')} size="small">
            Abrir empresas
          </RouteActionButton>
        </Box>
      )}
    </PageShell>
  );
};

export default VaccinationPendenciesPage;
